/**
 * useTier / useFieldAccess — who is allowed to work in BOTH shells.
 *
 * Theo has two shells: `/home` (OfficeLayout, ~93 routes) and `/tech`
 * (TechLayout, ~24 routes, behind TechAuthGuard). A Pro tenant keeps them
 * apart on purpose — office staff and field techs are different people, often
 * on shared devices.
 *
 * A **Lite** tenant is a 1–3 person operation where that split is nonsense:
 * the person who wrote the quote is the person who drives to the job. On a
 * Lite tenant everyone is dual-access by definition, so nobody has to be
 * flagged individually.
 *
 * This is an ENTITLEMENT check, not a security boundary. It decides whether
 * the switcher renders and whether a surface change is offered. Firestore
 * rules remain the only real boundary — as with the existing `user_role` key,
 * a determined user can edit localStorage, and could before this existed.
 *
 * @see src/lib/session.ts — enterFieldSurface / enterOfficeSurface
 * @see docs/TRADE_PACKS.md — tier vs trade pack vs feature packs
 */

import { useMemo } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { useTeam } from '@/hooks/useTeam';
import { getSession } from '@/lib/session';
import { isDualAccessFallback } from '@/data/team';

export type TenantTier = 'lite' | 'pro';

/**
 * The tenant's tier. Defaults to `'pro'` when unset — every existing tenant
 * predates the field and must keep the split shells.
 */
export function useTier(): TenantTier {
  const { tenant } = useTenant();
  return tenant?.tier === 'lite' ? 'lite' : 'pro';
}

/** Convenience: `useTier() === 'lite'`. */
export function useIsLite(): boolean {
  return useTier() === 'lite';
}

export interface FieldAccessResult {
  /** May this user move between the office and field shells? */
  canUseBothShells: boolean;
  /** Why — useful for copy and for telling a tenant-wide grant from a
   *  per-person one. `null` when they can't. */
  reason: 'lite-tenant' | 'dual-access' | null;
}

/**
 * Whether the signed-in user may use both shells.
 *
 * Two independent grants:
 *   - the tenant is Lite, so the split doesn't apply to anyone, or
 *   - this specific person carries `dualAccess` on their user doc (the Pro
 *     case — a director who also runs jobs).
 *
 * Tenant tier is checked FIRST and without touching `team`, which loads
 * async: on a Lite tenant the switcher must be there on first paint rather
 * than popping in a moment later.
 */
export function useFieldAccess(): FieldAccessResult {
  const isLite = useIsLite();
  const { team } = useTeam();
  const session = getSession();

  return useMemo(() => {
    if (isLite) return { canUseBothShells: true, reason: 'lite-tenant' as const };

    const { userId, userName } = session;
    if (!userId && !userName) return { canUseBothShells: false, reason: null };

    const flagged = userId ? team.some((m) => m.id === userId && m.dualAccess === true) : false;
    // TODO(saas-readiness): isDualAccessFallback matches a hardcoded first-name
    // list. It exists because some user docs never got the dualAccess flag
    // backfilled, and dropping it here would take the switcher away from the
    // people who need it most. Delete once those docs carry the flag.
    if (flagged || isDualAccessFallback(userName ?? undefined)) {
      return { canUseBothShells: true, reason: 'dual-access' as const };
    }
    return { canUseBothShells: false, reason: null };
  }, [isLite, team, session]);
}
